import { useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { FnrPopoverOption, FnrPopoverOptionsList } from "UI/FnrPopover2";

const mockData = {
  123: {
    groupName: "Group A",
    groupID: 123,
    boards: {
      123: {
        boardTitle: "Board A-1",
        boardID: 123,
        columns: {
          123: { columnTitle: "Column A-1-A", columnID: 123 },
          456: { columnTitle: "Column A-1-B", columnID: 456 },
          789: { columnTitle: "Column A-1-C", columnID: 789 },
        },
      },
      456: {
        boardTitle: "Board A-2",
        boardID: 456,
        columns: {
          234: { columnTitle: "Column A-2-A", columnID: 234 },
          567: { columnTitle: "Column A-2-B", columnID: 567 },
        },
      },
    },
  },
  456: {
    groupName: "Group B",
    groupID: 456,
    boards: {
      789: {
        boardTitle: "Board B-1",
        boardID: 789,
        columns: {
          890: { columnTitle: "Column B-1-A", columnID: 890 },
          345: { columnTitle: "Column B-1-B", columnID: 345 },
          678: { columnTitle: "Column B-1-C", columnID: 678 },
        },
      },
    },
  },
  789: {
    groupName: "Group C",
    groupID: 789,
    boards: {
      234: {
        boardTitle: "Board C-1",
        boardID: 234,
        columns: {
          345: { columnTitle: "Column C-1-A", columnID: 345 },
          678: { columnTitle: "Column C-1-B", columnID: 678 },
        },
      },
      567: {
        boardTitle: "Board C-2",
        boardID: 567,
        columns: {
          912: { columnTitle: "Column C-2-A", columnID: 912 },
          913: { columnTitle: "Column C-2-B", columnID: 913 },
        },
      },
    },
  },
};

export default function AddColumnShared({
  groups = {},
  setReadyToSave,
  selectedGroupID,
  setSelectedGroupID,
  selectedBoardID,
  setSelectedBoardID,
  selectedColumnID,
  setSelectedColumnID,
}) {
  const groupButtonRef = useRef();
  const boardButtonRef = useRef();
  const columnButtonRef = useRef();
  const [showGroupSelectList, setShowGroupSelectList] = useState(false);
  const [showBoardSelectList, setShowBoardSelectList] = useState(false);
  const [showColumnSelectList, setShowColumnSelectList] = useState(false);

  useEffect(() => {
    setReadyToSave(selectedGroupID && selectedBoardID && selectedColumnID);
  }, [selectedGroupID, selectedBoardID, selectedColumnID, setReadyToSave]);

  const closeAllSelectLists = () => {
    setShowGroupSelectList(false);
    setShowBoardSelectList(false);
    setShowColumnSelectList(false);
  };

  const toggleGroupPopover = () => {
    if (!getGroups().length) return;
    setShowBoardSelectList(false);
    setShowColumnSelectList(false);
    setShowGroupSelectList((prev) => !prev);
  };

  const toggleBoardPopover = () => {
    if (!selectedGroupID) return;
    setShowGroupSelectList(false);
    setShowColumnSelectList(false);
    setShowBoardSelectList((prev) => !prev);
  };

  const toggleColumnPopover = () => {
    if (!selectedGroupID || !selectedBoardID) return;
    setShowGroupSelectList(false);
    setShowBoardSelectList(false);
    setShowColumnSelectList((prev) => !prev);
  };

  const handleGroupSelect = (groupID) => {
    setSelectedGroupID(groupID);
    setSelectedBoardID(null);
    setSelectedColumnID(null);
    closeAllSelectLists();
    if (groupID in groups) {
      const group = groups[groupID] || null;
      const { boards } = group;
      if (Object.keys(boards || {}).length === 1) {
        const board = Object.values(boards)[0];
        setSelectedBoardID(board?.boardID || null);
        const { columns } = board;
        if (Object.keys(columns || {}).length === 1) {
          const column = Object.values(columns)[0];
          setSelectedColumnID(column?.columnID || null);
        }
      }
    }
  };

  const handleBoardSelect = (boardID) => {
    setSelectedBoardID(boardID);
    setSelectedColumnID(null);
    closeAllSelectLists();
    const group = groups[selectedGroupID] || {};
    const { boards } = group;
    if (boards && boardID in boards) {
      const board = boards[boardID] || {};
      const { columns } = board;
      if (Object.keys(columns || {}).length === 1) {
        const column = Object.values(columns)[0];
        setSelectedColumnID(column?.columnID || null);
      }
    }
  };

  const handleColumnSelect = (columnID) => {
    setSelectedColumnID(columnID);
    closeAllSelectLists();
  };

  const getSelectedGroup = () => {
    if (!groups || !Object.keys(groups || {}).length || !selectedGroupID)
      return null;
    return groups[selectedGroupID] || null;
  };

  const getSelectedBoard = () => {
    const group = getSelectedGroup();
    if (
      !group?.boards ||
      !Object.keys(group?.boards || {}).length ||
      !selectedBoardID
    )
      return null;
    return group.boards[selectedBoardID] || null;
  };

  const getSelectedColumn = () => {
    const board = getSelectedBoard();
    if (
      !board?.columns ||
      !Object.keys(board?.columns || {}).length ||
      !selectedColumnID
    )
      return null;
    return board.columns[selectedColumnID] || null;
  };

  const getSelectedGroupName = () => {
    if (!selectedGroupID) return "Select a group";
    const group = getSelectedGroup();
    if (!group) return "";
    return group?.groupName || "";
  };

  const getSelectedBoardName = () => {
    if (!selectedBoardID) return "Select a report";
    const board = getSelectedBoard();
    if (!board) return "";
    return board?.boardTitle || "";
  };

  const getSelectedColumnName = () => {
    if (!selectedColumnID) return "Select a column";
    const column = getSelectedColumn();
    if (!column) return "";
    return column?.columnTitle || "";
  };

  const getGroups = () => {
    if (!groups) return [];
    const groupsArr = Object.values(groups || {});
    if (!groupsArr?.length) return [];
    return groupsArr;
  };

  const getSelectedGroupBoards = () => {
    const group = getSelectedGroup();
    if (!group || !group.boards) return [];
    const boards = Object.values(group.boards || {});
    if (!boards?.length) return [];
    return boards;
  };

  const getSelectedBoardColumns = () => {
    const board = getSelectedBoard();
    if (!board || !board.columns) return [];
    const columns = Object.values(board.columns || {});
    if (!columns?.length) return [];
    return columns;
  };

  return (
    <Wrapper onClick={(e) => e.stopPropagation()}>
      <SelectListWrapper>
        <Icon className="fad fa-users-class" />
        <InnerWrapper>
          <Label>Group</Label>
          <Selection
            ref={groupButtonRef}
            onClick={toggleGroupPopover}
            disabled={!getGroups().length}
          >
            <span>{getSelectedGroupName()}</span>
            <SelectIcon
              className={
                showGroupSelectList
                  ? "fas fa-chevron-right rotate"
                  : "fas fa-chevron-right"
              }
            />
          </Selection>
          <FnrPopoverOptionsList
            show={showGroupSelectList}
            buttonRef={groupButtonRef}
          >
            {getGroups().map((group) => (
              <FnrPopoverOption
                key={group.groupID}
                disabled={selectedGroupID === group.groupID}
                onClick={() => handleGroupSelect(group.groupID)}
              >
                {group.groupName}
              </FnrPopoverOption>
            ))}
          </FnrPopoverOptionsList>
        </InnerWrapper>
      </SelectListWrapper>

      <SelectListWrapper>
        <Icon className="fad fa-table" />
        <InnerWrapper>
          <Label>Report</Label>
          <Selection
            ref={boardButtonRef}
            onClick={toggleBoardPopover}
            disabled={!selectedGroupID}
          >
            <span>{getSelectedBoardName()}</span>
            <SelectIcon
              className={
                showBoardSelectList
                  ? "fas fa-chevron-right rotate"
                  : "fas fa-chevron-right"
              }
            />
          </Selection>
          <FnrPopoverOptionsList
            show={showBoardSelectList}
            onShouldClose={() => setShowBoardSelectList(false)}
            buttonRef={boardButtonRef}
          >
            {getSelectedGroupBoards().map((board) => (
              <FnrPopoverOption
                key={board.boardID}
                disabled={selectedBoardID === board.boardID}
                onClick={() => handleBoardSelect(board.boardID)}
              >
                {board.boardTitle}
              </FnrPopoverOption>
            ))}
          </FnrPopoverOptionsList>
        </InnerWrapper>
      </SelectListWrapper>

      <SelectListWrapper>
        <Icon className="fas fa-columns" />
        <InnerWrapper>
          <Label>Column</Label>
          <Selection
            ref={columnButtonRef}
            onClick={toggleColumnPopover}
            disabled={!selectedGroupID || !selectedBoardID}
          >
            <span>{getSelectedColumnName()}</span>
            <SelectIcon
              className={
                showColumnSelectList
                  ? "fas fa-chevron-right rotate"
                  : "fas fa-chevron-right"
              }
            />
          </Selection>
          <FnrPopoverOptionsList
            show={showColumnSelectList}
            onShouldClose={() => setShowColumnSelectList(false)}
            buttonRef={columnButtonRef}
          >
            {getSelectedBoardColumns().map((column) => (
              <FnrPopoverOption
                key={column.columnID}
                disabled={selectedColumnID === column.columnID}
                onClick={() => handleColumnSelect(column.columnID)}
              >
                {column.columnTitle}
              </FnrPopoverOption>
            ))}
          </FnrPopoverOptionsList>
        </InnerWrapper>
      </SelectListWrapper>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const Selection = styled.div`
  font-weight: 400;
  cursor: pointer;
  ${(props) =>
    props.disabled &&
    `
    opacity: 0.6;
    cursor: not-allowed;
  `}
`;
const SelectIcon = styled.span`
  margin-left: 8px;
  font-size: 14px;
  transition: transform 75ms;

  &.rotate {
    transform: rotate(90deg);
  }
`;

function AddColumnSelectList() {
  return (
    <SelectListWrapper>
      <Icon className="" />
      <InnerWrapper></InnerWrapper>
    </SelectListWrapper>
  );
}

const SelectListWrapper = styled.div`
  display: flex;
  gap: 12px;

  box-sizing: border-box;
  width: 100%;
  position: relative;
  color: #475b68;
  background: linear-gradient(
    -2deg,
    #ffffff 60%,
    #fdfdfd,
    #fbfbfc,
    #f8f9fa,
    #f6f7f8
  );
  border-radius: 7px;
  border: 1px solid #98a4a5;
  padding: 6px;
`;

const Icon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 22px;
  width: 30px;
`;

const InnerWrapper = styled.div``;

const Label = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  color: #828c8d;
  font-weight: 400;
  line-height: 15px;
`;
